import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Truck, Trash2 } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, BulkAction, PaginationData, DirtyCell } from '@/components/admin/DataTable';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface Supplier {
  id: string;
  name: string;
  code: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  api_type: string;
  default_lead_time_days: number;
  is_active: boolean;
  active_products_count: number;
  created_at: string;
}

interface SuppliersResponse {
  suppliers: Supplier[];
  pagination: PaginationData;
}

const columns: ColumnDef<Supplier>[] = [
  {
    id: 'name',
    header: 'Supplier',
    accessorKey: 'name',
    sortable: true,
    cell: ({ row }) => (
      <Link to={`/admin/suppliers/${row.id}`} className="block group">
        <div className="font-medium text-gray-900 group-hover:text-blue-600">{row.name}</div>
        <div className="text-sm text-gray-500">{row.code}</div>
      </Link>
    ),
  },
  {
    id: 'contact',
    header: 'Contact',
    cell: ({ row }) => (
      <div>
        {row.contact_name && <div className="text-sm text-gray-900">{row.contact_name}</div>}
        {row.contact_email && <div className="text-sm text-gray-500">{row.contact_email}</div>}
      </div>
    ),
  },
  {
    id: 'api_type',
    header: 'Integration',
    accessorKey: 'api_type',
    cell: ({ row }) => {
      const labels: Record<string, string> = { none: 'Manual', email: 'Email', api: 'API', webhook: 'Webhook' };
      const variants: Record<string, any> = { none: 'gray', email: 'yellow', api: 'green', webhook: 'blue' };
      return <Badge variant={variants[row.api_type] || 'gray'}>{labels[row.api_type] || row.api_type}</Badge>;
    },
  },
  {
    id: 'active_products_count',
    header: 'Products',
    accessorKey: 'active_products_count',
    align: 'right',
    cell: ({ row }) => (
      <span className="text-sm text-gray-600">{row.active_products_count} linked</span>
    ),
  },
  {
    id: 'default_lead_time_days',
    header: 'Lead Time',
    accessorKey: 'default_lead_time_days',
    align: 'right',
    editable: true,
    editType: 'number',
    cell: ({ row }) => (
      <span className="text-sm text-gray-600">{row.default_lead_time_days} days</span>
    ),
  },
  {
    id: 'is_active',
    header: 'Status',
    accessorKey: 'is_active',
    editable: true,
    editType: 'select',
    editOptions: [
      { label: 'Active', value: 'true' },
      { label: 'Inactive', value: 'false' },
    ],
    cell: ({ row }) => (
      <Badge variant={row.is_active ? 'green' : 'gray'}>
        {row.is_active ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    id: 'contact_phone',
    header: 'Phone',
    accessorKey: 'contact_phone',
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">{row.contact_phone || '-'}</span>
    ),
  },
  {
    id: 'website',
    header: 'Website',
    accessorKey: 'website',
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) =>
      row.website ? (
        <a href={row.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate block max-w-[200px]">
          {row.website.replace(/^https?:\/\//, '')}
        </a>
      ) : (
        <span className="text-sm text-gray-400">-</span>
      ),
  },
];

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  const { state, updateState, markDirty } = useDataTableState({
    storageKey: 'admin-suppliers',
    defaultSort: 'name',
    defaultLimit: 25,
    columns,
  });

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.set('page', state.page.toString());
        params.set('limit', state.limit.toString());
        if (state.search) params.set('q', state.search);
        const response = await api.get<SuppliersResponse>(`/admin/suppliers?${params.toString()}`);
        setSuppliers(response.suppliers);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load suppliers');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSuppliers();
  }, [state.page, state.limit, state.search, state.sort, state.order]);

  const handleBatchSave = useCallback(async (changes: DirtyCell[]) => {
    const byRow = new Map<string, Record<string, unknown>>();
    for (const cell of changes) {
      if (!byRow.has(cell.rowId)) byRow.set(cell.rowId, {});
      const fields = byRow.get(cell.rowId)!;
      if (cell.columnId === 'is_active') {
        fields[cell.columnId] = cell.value === 'true' || cell.value === true;
      } else if (cell.columnId === 'default_lead_time_days') {
        fields[cell.columnId] = parseInt(String(cell.value), 10);
      } else {
        fields[cell.columnId] = cell.value;
      }
    }

    const errors: string[] = [];
    for (const [rowId, fields] of byRow) {
      try {
        await api.put(`/admin/suppliers/${rowId}`, fields);
        setSuppliers((prev) =>
          prev.map((s) => (s.id === rowId ? { ...s, ...fields } : s))
        );
      } catch (err: any) {
        errors.push(err.message || `Failed to update supplier ${rowId}`);
      }
    }

    if (errors.length > 0) {
      addToast(`${errors.length} update(s) failed`, 'error');
      throw new Error('Some updates failed');
    }
    addToast(`${byRow.size} supplier(s) updated`, 'success');
  }, [addToast]);

  const handleDirtyChange = useCallback(
    (rowId: string, columnId: string, value: unknown, originalValue: unknown) => {
      markDirty(rowId, columnId, value, originalValue);
    },
    [markDirty]
  );

  const bulkActions: BulkAction<Supplier>[] = [
    {
      id: 'deactivate',
      label: 'Deactivate',
      icon: <Trash2 className="w-4 h-4 mr-1" />,
      variant: 'danger',
      confirmMessage: 'Are you sure you want to deactivate the selected suppliers?',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.delete(`/admin/suppliers/${row.id}`);
        }
        setSuppliers((prev) => prev.filter((s) => !rows.some((r) => r.id === s.id)));
        addToast(`${rows.length} supplier(s) deactivated`, 'success');
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Suppliers</h1>
          <p className="text-gray-600 mt-1">Manage dropshipping suppliers and integrations</p>
        </div>
        <Link to="/admin/suppliers/new">
          <Button variant="primary" size="md">
            <Plus className="w-5 h-5 mr-2" />
            Add Supplier
          </Button>
        </Link>
      </div>

      <DataTable
        columns={columns}
        data={suppliers}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        onBatchSave={handleBatchSave}
        onDirtyChange={handleDirtyChange}
        bulkActions={bulkActions}
        searchPlaceholder="Search suppliers by name, code, or email..."
        emptyState={
          <div className="p-12 text-center">
            <Truck className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No suppliers yet</h3>
            <p className="text-gray-600 mb-6">Add your first supplier to start dropshipping</p>
            <Link to="/admin/suppliers/new">
              <Button variant="primary" size="md">
                <Plus className="w-5 h-5 mr-2" />
                Add Supplier
              </Button>
            </Link>
          </div>
        }
      />
    </div>
  );
};

export default Suppliers;
